/** @jsxImportSource hono/jsx/dom */
import type { FC } from 'hono/jsx'
import { useCallback, useEffect, useRef, useState } from 'hono/jsx'
import { ApiError, fetchWordState, register, wordPath, type WordState } from './api'
import { setNavigationHandler } from './router'
import { getVoted, getVoterId, setVoted } from './storage'
import { TurnstileField } from './turnstile'

type Message = { kind: 'info' | 'error'; text: string }

export const WordApp: FC<{ initial: WordState; siteKey: string }> = ({ initial, siteKey }) => {
  const [state, setState] = useState<WordState>(initial)
  const [voted, setVotedValue] = useState<string | null>(getVoted(initial.word))
  const [message, setMessage] = useState<Message | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(false)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const turnstileRef = useRef<HTMLDivElement | null>(null)
  const fieldRef = useRef<TurnstileField | null>(null)

  useEffect(() => {
    const field = new TurnstileField()
    fieldRef.current = field
    if (turnstileRef.current) {
      void field.mount(turnstileRef.current, siteKey).catch(() => {
        setMessage({ kind: 'error', text: '認証ウィジェットを読み込めませんでした' })
      })
    }
    return () => field.destroy()
  }, [])

  const applyState = useCallback((next: WordState, push: boolean) => {
    setState(next)
    setVotedValue(getVoted(next.word))
    setMessage(null)
    if (inputRef.current) inputRef.current.value = ''
    document.title = `${next.word} の読み方 - YOMIKATA`
    if (push) history.pushState({ word: next.word }, '', wordPath(next.word))
  }, [])

  // 検索結果やサイドバーからの遷移を、画面遷移なしで受け取る
  useEffect(() => {
    setNavigationHandler((word, options) => {
      if (options.state) {
        applyState(options.state, options.push)
        return
      }
      setLoading(true)
      fetchWordState(word)
        .then((next) => applyState(next, options.push))
        .catch(() => location.assign(wordPath(word)))
        .finally(() => setLoading(false))
    })
    return () => setNavigationHandler(null)
  }, [applyState])

  const submit = useCallback(
    async (pronunciation: string) => {
      const field = fieldRef.current
      if (busy || !field || pronunciation.trim() === '') return
      setBusy(true)
      setMessage(null)
      try {
        const token = await field.getToken()
        const result = await register({
          word: state.word,
          pronunciation,
          voterId: getVoterId(),
          turnstileToken: token,
        })
        // トークンは単回使用のため、送信ごとに取り直す
        field.reset()
        setVoted(result.word, result.voted)
        setState({ word: result.word, pronunciations: result.pronunciations, registered: true })
        setVotedValue(result.voted)
        setMessage({ kind: 'info', text: `「${result.voted}」に投票しました` })
        if (inputRef.current) inputRef.current.value = ''
      } catch (err) {
        fieldRef.current?.reset()
        setMessage({
          kind: 'error',
          text: err instanceof ApiError ? err.message : '通信に失敗しました',
        })
      } finally {
        setBusy(false)
      }
    },
    [busy, state.word]
  )

  const onSubmitForm = useCallback(
    (event: Event) => {
      event.preventDefault()
      void submit(inputRef.current?.value ?? '')
    },
    [submit]
  )

  return (
    <div class="word-app">
      <h1 class="word-title">{state.word}</h1>

      {loading ? <p class="loading">読み込み中…</p> : null}
      {message ? (
        <p class={message.kind === 'error' ? 'notice error-text' : 'notice info-text'} role="status">
          {message.text}
        </p>
      ) : null}

      {state.pronunciations.length === 0 ? (
        <p class="empty notice">まだ登録がありません</p>
      ) : (
        <ul class="pronunciations">
          {state.pronunciations.map((row) => {
            const isVoted = voted === row.pronunciation
            return (
              <li key={row.pronunciation} class={isVoted ? 'pronunciation voted' : 'pronunciation'}>
                <span class="reading">{row.pronunciation}</span>
                <span class="count">
                  {row.count}
                  <span class="count-unit">票</span>
                </span>
                {isVoted ? <span class="voted-badge">投票済み</span> : null}
                <button
                  type="button"
                  disabled={busy || isVoted}
                  onClick={() => void submit(row.pronunciation)}
                >
                  投票する
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <section class="register-inline" aria-labelledby="add-pronunciation-heading">
        <h2 id="add-pronunciation-heading">新しい読み方を登録</h2>
        <form class="stack" onSubmit={onSubmitForm}>
          <label class="field">
            <span class="field-label">読み方</span>
            <input
              ref={inputRef}
              type="text"
              name="pronunciation"
              required
              maxlength={100}
              autocomplete="off"
              placeholder="ギット"
            />
          </label>
          <div class="turnstile-holder" ref={turnstileRef} />
          <button type="submit" disabled={busy}>
            {busy ? '送信中…' : '登録する'}
          </button>
        </form>
      </section>
    </div>
  )
}
